/**
 * HistoryPage — 前台「记录」Tab
 * 改为 OCR 识别记录，对应 demo view-user-ocr 效果：
 *   - 近 60 天的上传批次列表
 *   - 识别中：进度条 + 已识别 N/M 张
 *   - 识别完成：成功/重复/无效/失败 badge
 *   - 点击展开批次详情：成功配对（手机号+图片）+ 待处理（手动填写按钮）
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import TabBar from '../components/TabBar';
import Modal from '../components/Modal';
import OcrGroupDetail from '../components/OcrGroupDetail';
import api from '../utils/api';
import { useSocket } from '../contexts/SocketContext';
import { useToast } from '../components/Toast';
import { useNavigate } from 'react-router-dom';
import { MailX, Loader, X, AlertTriangle, CheckCircle2, ArrowLeft } from 'lucide-react';

export default function HistoryPage() {
  const navigate = useNavigate();
  const [groups, setGroups]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expanded, setExpanded]     = useState({}); // groupUUID -> bool
  const [manualTarget, setManualTarget] = useState(null); // { imageId }
  const [manualPhone, setManualPhone]       = useState('');
  const [manualLoading, setManualLoading]   = useState(false);
  const [manualCheckResult, setManualCheckResult] = useState(null); // 派单库实时校验结果
  const { subscribe } = useSocket();
  const { showToast } = useToast();
  const [ocrDoneBanner, setOcrDoneBanner] = useState(null);
  const ocrBannerTimer = useRef(null);
  // 每个批次的实时识别进度（来自 ocr_progress 事件）：groupUUID -> { done, total }
  const [liveProgress, setLiveProgress] = useState({});
  // 已处理过的「识别完成」事件（groupUUID -> 时间戳），用于去重：
  // 管理员会同时在 admins 与 user_ 两个房间，同一批次的 ocr_batch_done 会收到两次。
  const handledDoneRef = useRef(new Map());

  const fetchGroups = useCallback(async () => {
    try {
      const res = await api.get('/images/my-upload-groups');
      setGroups(res.data?.groups || res.data || []);
    } catch (e) {
      // 静默失败
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event, payload) => {
      if (event === 'ocr_progress') {
        // 用事件里的真实 done/total 驱动进度条动画，而不是等批次跑完才刷新
        if (payload?.groupUUID) {
          setLiveProgress(p => ({
            ...p,
            [payload.groupUUID]: { done: payload.done || 0, total: payload.total || 0 },
          }));
        }
        clearTimeout(window._histOcrTimer);
        window._histOcrTimer = setTimeout(() => fetchGroups(), 800);
      } else if (['data_refresh', 'image_stats_refresh'].includes(event)) {
        fetchGroups();
      }
      if (event === 'ocr_batch_done') {
        // 去重：同一批次的完成事件可能因多房间订阅收到两次，4 秒内重复的直接忽略，
        // 避免横幅/展开等被触发两遍。
        const gid = payload?.groupUUID;
        const nowTs = Date.now();
        if (gid) {
          const last = handledDoneRef.current.get(gid);
          if (last && nowTs - last < 4000) return;
          handledDoneRef.current.set(gid, nowTs);
        }
        fetchGroups();
        const s = payload?.stats || {};
        // 该批次已完成，清掉它的实时进度缓存
        if (gid) {
          setLiveProgress(p => { const n = { ...p }; delete n[gid]; return n; });
          // 自动展开刚完成的批次
          setExpanded(p => ({ ...p, [gid]: true }));
        }
        // 只用页内横幅提示一次（此前还额外弹了一条同样内容的 toast，等于提示两遍）
        if (ocrBannerTimer.current) clearTimeout(ocrBannerTimer.current);
        setOcrDoneBanner({ stats: s, groupUUID: gid });
        const delay = (s.invalid || 0) + (s.failed || 0) > 0 ? 10000 : 5000;
        ocrBannerTimer.current = setTimeout(() => setOcrDoneBanner(null), delay);
      }
    });
  }, [subscribe, fetchGroups]);

  // 格式化日期：2026-09-19 → 09-19
  function fmtDate(dateStr) {
    return dateStr ? dateStr.slice(5) : '';
  }

  // 按上传日期分组
  const byDate = groups.reduce((acc, g) => {
    const d = g.uploadDate || g.upload_date || '';
    if (!acc[d]) acc[d] = [];
    acc[d].push(g);
    return acc;
  }, {});
  const dates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        {/* 从“我的”进来就回“我的”，从工作台的“查看识别记录”进来就回工作台；直接打开（没有上一页）时回“我的” */}
        <button onClick={() => (window.history.state && window.history.state.idx > 0 ? navigate(-1) : navigate('/me'))}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1, fontWeight: 600 }}>OCR 识别记录</span>
        <span style={{ fontSize: 12, color: 'var(--c-text-tertiary)' }}>近 7 天上传批次</span>
      </div>

      {/* OCR 完成横幅：识别完成时在本页顶部显示 */}
      {ocrDoneBanner && (() => {
        const s = ocrDoneBanner.stats || {};
        const hasBad = (s.invalid || 0) + (s.failed || 0) > 0;
        const parts = [];
        if (s.qualified > 0) parts.push(`成功 ${s.qualified} 组`);
        if (s.duplicate > 0) parts.push(`重复 ${s.duplicate} 张`);
        if (hasBad) parts.push(`待处理 ${(s.invalid||0)+(s.failed||0)} 张`);
        return (
          <div style={{
            margin: '8px 16px 0',
            background: hasBad ? 'rgba(255,149,0,0.08)' : 'rgba(52,199,89,0.08)',
            border: `1px solid ${hasBad ? 'rgba(255,149,0,0.3)' : 'rgba(52,199,89,0.3)'}`,
            borderRadius: 12, padding: '11px 14px',
            display: 'flex', alignItems: 'center', gap: 10,
            animation: 'slideUpBanner .2s ease',
          }}>
            <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{hasBad ? <AlertTriangle size={18} color='var(--c-accent)' /> : <CheckCircle2 size={18} color='var(--c-success)' />}</span>
            <div style={{ flex: 1, fontSize: 13, lineHeight: 1.4 }}>
              <strong>识别完成：</strong>{parts.join('，')}
              {hasBad && <span style={{ color: 'var(--c-accent)', display: 'block', fontSize: 12, marginTop: 2 }}>
                有待处理项，点击批次展开后可手动填写或重新识别
              </span>}
            </div>
            <button onClick={() => setOcrDoneBanner(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-tertiary)', padding: 2 }}>
              <X size={14} />
            </button>
          </div>
        );
      })()}

      <div style={{ padding: '12px 16px' }}>
        {loading ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>
        ) : groups.length === 0 ? (
          <div className="card empty-state">
            <div className="empty-icon"><MailX size={40} color="var(--c-text-secondary)" strokeWidth={1.2} /></div>
            <p>暂无上传记录</p>
          </div>
        ) : dates.map(date => (
          <div key={date}>
            {/* 日期分隔 */}
            <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', fontWeight: 600, margin: '12px 2px 6px' }}>
              {date}
            </div>

            {byDate[date].map(group => {
              const ocr = group.ocr || {};
              const gid = group.groupUUID || group.id;
              const live = liveProgress[gid];
              const ocrDone    = ocr.done && ocr.total > 0;
              // 识别中：完成时间未写 + （批次已标注总数 或 收到过实时进度事件）
              const ocrRunning = !ocr.done && (ocr.total > 0 || (live && live.total > 0));
              const isOpen     = !!expanded[gid];
              const badCount   = (ocr.invalid || 0) + (ocr.failed || 0);

              // 进度百分比（识别中时）：优先用实时事件的 done/total，
              // 没有事件时退回按最终分类列估算（仅作兜底）。
              const fallbackDone = (ocr.qualified || 0) * 2 + (ocr.duplicate || 0) + (ocr.invalid || 0) + (ocr.failed || 0);
              const doneCount = live ? live.done : fallbackDone;
              const totalCount = (live && live.total > 0) ? live.total : ocr.total;
              const pct = totalCount > 0 ? Math.min(100, Math.round(doneCount / totalCount * 100)) : 0;

              return (
                <div key={group.id} style={{
                  border: '1px solid var(--c-border)',
                  borderRadius: 14,
                  overflow: 'hidden',
                  marginBottom: 10,
                  background: 'var(--c-surface)',
                }}>
                  {/* 批次头：点击展开（仅识别完成时可展开） */}
                  <div
                    style={{ padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 10, cursor: ocrDone ? 'pointer' : 'default' }}
                    onClick={() => ocrDone && !group.is_exported && setExpanded(p => ({ ...p, [group.groupUUID || group.id]: !p[group.groupUUID || group.id] }))}
                  >
                    <div style={{ flex: 1 }}>
                      {/* 第一行：批次标题 + 状态 badge */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: ocrRunning ? 6 : (ocrDone ? 5 : 0) }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>
                          {fmtDate(group.uploadDate || group.upload_date)} 第{group.seq}批 · {group.image_count || group.imageCount || 0} 张
                        </span>
                        {ocrRunning && (
                          <span style={{ fontSize: 10, color: 'var(--c-primary)', display: 'flex', alignItems: 'center', gap: 3, fontWeight: 600 }}>
                            <Loader size={9} className="spin" />识别中
                          </span>
                        )}
                        {ocrDone && (
                          <span style={{ fontSize: 10, color: 'var(--c-success)', display: 'flex', alignItems: 'center', gap: 2 }}>
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>识别完成
                          </span>
                        )}
                        {!!group.is_exported && (
                          <span style={{ fontSize: 10, color: 'var(--c-text-tertiary)', background: 'var(--c-bg)', border: '1px solid var(--c-border)', borderRadius: 6, padding: '1px 6px' }}>
                            图片已清理
                          </span>
                        )}
                        {!ocr.total && (
                          <span style={{ fontSize: 10, color: 'var(--c-text-tertiary)' }}>等待识别…</span>
                        )}
                      </div>

                      {/* 进度条（识别中） */}
                      {ocrRunning && (
                        <>
                          <div style={{ height: 3, background: 'var(--c-border)', borderRadius: 2, overflow: 'hidden', marginBottom: 4 }}>
                            <div style={{ height: '100%', background: 'var(--c-primary)', borderRadius: 2, width: `${pct}%`, transition: 'width .5s' }} />
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)' }}>
                            已识别 {doneCount} / {totalCount} 张 · {pct}%
                          </div>
                        </>
                      )}

                      {/* 识别完成：结果 badges */}
                      {ocrDone && (
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          {(ocr.qualified || 0) > 0 && (
                            <span style={{ fontSize: 10, background: 'rgba(52,199,89,.12)', color: 'var(--c-success)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>
                              成功 {ocr.qualified} 组
                            </span>
                          )}
                          {(ocr.duplicate || 0) > 0 && (
                            <span style={{ fontSize: 10, background: 'rgba(255,149,0,.12)', color: 'var(--c-accent)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>
                              重复 {ocr.duplicate} 张
                            </span>
                          )}
                          {(ocr.invalid || 0) > 0 && (
                            <span style={{ fontSize: 10, background: 'rgba(255,59,48,.1)', color: 'var(--c-danger)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>
                              无效 {ocr.invalid} 张
                            </span>
                          )}
                          {(ocr.failed || 0) > 0 && (
                            <span style={{ fontSize: 10, background: 'var(--c-bg)', color: 'var(--c-text-secondary)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>
                              失败 {ocr.failed} 张
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* 展开箭头：图片未清理时才显示 */}
                    {ocrDone && !group.is_exported && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--c-text-tertiary)" strokeWidth="2"
                        style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '.18s', flexShrink: 0, marginTop: 2 }}>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    )}
                  </div>

                  {/* 展开内容：调 OcrGroupDetail */}
                  {isOpen && ocrDone && (
                    <OcrGroupDetail
                      groupUUID={group.groupUUID || group.id}
                      imagesDeleted={!!group.is_exported}
                      badCount={badCount}
                      onManualFill={(imageId, prefillPhone) => {
                        setManualTarget({ imageId });
                        setManualPhone(prefillPhone || '');
                      }}
                      onReocr={async (imageId) => {
                        try {
                          const r = await api.post('/images/reocr-single', { imageId: String(imageId) });
                          showToast(r?.data?.message || '已加入重新识别队列', 'success');
                          setTimeout(fetchGroups, 1500);
                        } catch (err) {
                          showToast(err.response?.data?.error || '操作失败', 'error');
                        }
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* 手动填写手机号弹窗 */}
      {manualTarget && (
        <Modal show={true} onClose={() => setManualTarget(null)} title="手动填写手机号">
          <div style={{ padding: '4px 0 8px' }}>
            <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 12 }}>
              图片未能识别到号码，请手动输入：
            </p>
            <div style={{ position: 'relative' }}>
              <input
                className="input"
                type="tel"
                placeholder="输入11位手机号"
                maxLength={11}
                value={manualPhone}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 11);
                  setManualPhone(v);
                  setManualCheckResult(null);
                  if (v.length === 11 && /^1[3-9]\d{9}$/.test(v)) {
                    api.post('/images/check-phone', { phone: v })
                      .then(r => setManualCheckResult(r.data))
                      .catch(() => {});
                  }
                }}
                style={{ letterSpacing: '.06em', paddingRight: 44 }}
                autoFocus
              />
              <span style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                fontSize: 11, fontWeight: 600, pointerEvents: 'none',
                color: manualPhone.length === 11 ? 'var(--c-success)' : 'var(--c-text-tertiary)'
              }}>
                {manualPhone.length}/11
              </span>
            </div>
            {manualPhone.length > 0 && manualPhone.length < 11 && (
              <p style={{ fontSize: 11.5, color: 'var(--c-danger)', marginTop: 5 }}>
                还需再输入 {11 - manualPhone.length} 位
              </p>
            )}
            {manualCheckResult && !manualCheckResult.valid && (
              <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, fontSize: 12, background: 'rgba(255,59,48,.08)', color: 'var(--c-danger)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                {manualCheckResult.message}
              </div>
            )}
            {manualCheckResult && manualCheckResult.valid && (
              <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, fontSize: 12, background: 'rgba(52,199,89,.08)', color: 'var(--c-success)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                号码在派单库中，可以提交
              </div>
            )}
                        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-outline" style={{ flex: 1, height: 44 }}
                onClick={() => { setManualTarget(null); setManualPhone(''); setManualCheckResult(null); }}>取消</button>
              <button
                className="btn btn-primary"
                style={{ flex: 1, height: 44 }}
                disabled={!/^1[3-9]\d{9}$/.test(manualPhone) || manualLoading || (manualCheckResult && !manualCheckResult.valid)}
                onClick={async () => {
                  setManualLoading(true);
                  try {
                    const res = await api.post(`/images/ocr-edit/${manualTarget.imageId}`, { phone: manualPhone });
                    showToast('已保存手机号', 'success');
                    setManualTarget(null);
                    setManualCheckResult(null);
                    setTimeout(fetchGroups, 800);
                  } catch (err) {
                    const errData = err.response?.data;
                    const errMsg  = errData?.error || '保存失败';
                    // 422：OCR验证失败，图片已被锁定
                    if (err.response?.status === 422) {
                      showToast(errMsg, 'error');
                      // 有OCR原文片段时显示在弹窗里
                      if (errData?.ocrPreview) {
                        setManualCheckResult({ valid: false, message: `图片识别内容：「${errData.ocrPreview}」` });
                      } else {
                        setManualCheckResult({ valid: false, message: errMsg });
                      }
                      // 刷新列表让图片显示锁定状态
                      setTimeout(fetchGroups, 500);
                    } else {
                      showToast(errMsg, 'error');
                    }
                  } finally {
                    setManualLoading(false);
                  }
                }}
              >
                {manualLoading ? '保存中…' : '确认填写'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <TabBar mode="user" />
    </div>
  );
}
